import { ReactNode, useEffect, useMemo } from 'react'
import {
  initialQueryState,
  OptionsHelper,
  Permission,
  QueryFilter,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useAuth } from '../../../../../auth'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Adicionar"). */
  actions?: ReactNode
}

export const FormulariosListSearchComponent = ({ actions }: Props) => {
  const { state, updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { currentUser } = useAuth()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const isAdmin = currentUser?.grupo?.nome === Permission.Admin

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.status ? `ativo:${debouncedFilters.status.value}` : '',
        debouncedFilters.formulario ? `nome~*${debouncedFilters.formulario}*` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  // A instituição vai em `filters` (contrato do endpoint /formularios/empresa), não no `search`.
  const filtersEmpresa = useMemo(
    (): QueryFilter[] =>
      debouncedFilters.empresa
        ? [{ field: 'id', operator: '=', value: debouncedFilters.empresa.value }]
        : [],
    [debouncedFilters]
  )

  const filtrosAtuais = JSON.stringify(state.filters ?? [])
  useEffect(() => {
    if ((state.search ?? '') === search && filtrosAtuais === JSON.stringify(filtersEmpresa)) return
    updateState({
      ...initialQueryState,
      search,
      filters: filtersEmpresa,
    })
  }, [search, filtersEmpresa, state.search, filtrosAtuais, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.formulario && {
      key: 'formulario',
      label: t('formulario.label'),
      value: `“${filters.formulario}”`,
      onRemove: () => setFilter('formulario', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.status && {
      key: 'status',
      label: t('situacao.label'),
      value: filters.status.label,
      onRemove: () => setFilter('status', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  // Sem instituição o Admin não tem listagem: o resumo não mostra "0 encontrados".
  const summary =
    isAdmin && !filters.empresa
      ? undefined
      : isLoading
      ? 'Buscando…'
      : `${total} ${total === 1 ? 'formulário encontrado' : 'formulários encontrados'}`

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar filters={activeFilters} onClearAll={clearFilters} summary={summary} />
      }
    >
      <TableFilter label={t('formulario.label')} wide>
        <Input
          entity='formulario'
          value={filters.formulario}
          onChange={(e) => setFilter('formulario', e.target.value)}
        />
      </TableFilter>
      {isAdmin && (
        <TableFilter label={t('empresa.label')}>
          <CustomSelect
            entity='empresa'
            onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
            fetchOptions={OptionsHelper.getOptionsEmpresasAdmin}
            value={filters.empresa}
          />
        </TableFilter>
      )}
      <TableFilter label={t('situacao.label')}>
        <CustomSelect
          entity='situacao'
          onChange={(status?: IOption<boolean>) => setFilter('status', status ?? undefined)}
          options={OptionsHelper.statusCadastro}
          value={filters.status}
        />
      </TableFilter>
    </TableFilters>
  )
}
